import { useEffect, useState } from "react";
import type { StatsigOptions } from "@statsig/js-client";
import { StatsigClient } from "@statsig/js-client";
import { LocalOverrideAdapter } from "@statsig/js-local-overrides";
import { StatsigProvider } from "@statsig/react-bindings";

import type { StatsigProps } from "./server.utils";

interface Override {
  name: string;
  value: Record<string, unknown>;
}

export type ExperimentOverrides = Record<string, Override>;

function getLocalOverridesAdapter(value: ExperimentOverrides | null) {
  if (!value) {
    return undefined;
  }

  const overrideAdapter = new LocalOverrideAdapter();

  Object.values(value).forEach((override) => {
    overrideAdapter.overrideExperiment(override.name, override.value);
  });

  return overrideAdapter;
}

interface ExperimentProviderProps {
  apiKey: string;
  disabled?: boolean;
  experimentOverrides?: ExperimentOverrides | null;
  initData?: StatsigProps;
}

/**
 * A React component that provides a Statsig client to its children for A/B testing and experimentation.
 *
 * The component initializes the Statsig client on the client side, either using server-side props
 * or by creating a new instance based on an anonymous user ID retrieved from Segment. It wraps its children
 * with the `StatsigProvider` component if the client is successfully initialized, allowing them to access
 * the Statsig features.
 */
export function ExperimentProvider({
  children,
  initData,
  experimentOverrides,
  disabled,
  apiKey,
}: React.PropsWithChildren<ExperimentProviderProps>) {
  function getStatsigOptions(): StatsigOptions {
    const options: StatsigOptions = {
      networkConfig: {
        preventAllNetworkTraffic: disabled,
      },
      environment: {
        tier:
          process.env.NODE_ENV === "production" ? "production" : "development",
      },
    };

    if (experimentOverrides) {
      options.overrideAdapter = getLocalOverridesAdapter(experimentOverrides);
    }

    return options;
  }

  const [client] = useState<StatsigClient | null>(() => {
    if (!initData?.user) {
      return null;
    }

    const inst = new StatsigClient(apiKey, initData.user, getStatsigOptions());

    if (!initData?.values) {
      return inst;
    }

    inst.dataAdapter.setData(initData.values);
    inst.initializeSync();

    return inst;
  });

  useEffect(() => {
    if (!initData?.user) {
      return;
    }

    client?.initializeAsync();
  }, [initData?.user]);

  useEffect(() => {
    if (!initData?.values) {
      return;
    }

    if (!client) {
      return;
    }

    client.dataAdapter.setData(initData.values);
  }, [initData, client]);

  if (!client) {
    return children;
  }

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
}
